import React from 'react';
import { ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import { Link } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: {
    value: string | number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  link?: string;
}

export const StatCard: React.FC<StatCardProps> = ({ title, value, icon, change, link }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <div className="text-primary-500">{icon}</div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      
      {change && (
        <div className="flex items-center mt-2 text-xs">
          {change.type === 'increase' ? (
            <ArrowUp size={14} className="text-green-500 mr-1" />
          ) : change.type === 'decrease' ? (
            <ArrowDown size={14} className="text-red-500 mr-1" />
          ) : (
            <span className="w-3.5 h-3.5 mr-1" />
          )}
          <span className={
            change.type === 'increase' ? 'text-green-600' : 
            change.type === 'decrease' ? 'text-red-600' : 
            'text-gray-500'
          }>
            {change.value}
          </span>
        </div>
      )}
      
      {link && (
        <div className="flex items-center mt-2 text-xs text-primary-600">
          <ArrowRight size={14} className="mr-1" />
          <Link to={link}>Voir plus</Link>
        </div>
      )}
    </div>
  );
};

interface DashboardStatsProps {
  stats: {
    products: number;
    locations: number;
    blogPosts: number;
    partners: number;
  };
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <StatCard 
        title="Produits" 
        value={stats.products} 
        icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" y1="22" x2="12" y2="12"></line></svg>}
        change={{ value: "+2 cette semaine", type: "increase" }}
        link="/admin/products"
      />
      
      <StatCard 
        title="Stations" 
        value={stats.locations} 
        icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>}
        change={{ value: "Stable", type: "neutral" }}
        link="/admin/locations"
      />
      
      <StatCard 
        title="Articles" 
        value={stats.blogPosts} 
        icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>}
        change={{ value: "+1 ce mois", type: "increase" }}
        link="/admin/blog"
      />
      
      <StatCard 
        title="Partenaires" 
        value={stats.partners} 
        icon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"></path><rect x="3" y="4" width="18" height="18" rx="2"></rect><circle cx="12" cy="10" r="2"></circle><line x1="8" y1="2" x2="8" y2="4"></line><line x1="16" y1="2" x2="16" y2="4"></line></svg>}
        change={{ value: "Stable", type: "neutral" }}
        link="/admin/partners"
      />
    </div>
  );
};

export default DashboardStats;